import { ApiError } from "./client";
import type { ApiResponse } from "@/types/api";
import type { QuestionType } from "./reportTemplates";

// AI Prompt Builder v1.0 — frontend wire types. These mirror the backend's
// PromptDraftingDtos.cs and the two endpoints registered on
// ReportTemplatesController:
//   POST /api/admin/report-templates/{id}/draft-prompt
//   POST /api/admin/report-templates/draft-prompt   (no template ID yet)
//
// The drafting service is non-streaming. We hand-write fetch() instead of
// going through api.post so the call can be cancelled via AbortController
// (the existing api.post helper doesn't surface the request signal).

export interface FormQuestionInput {
  questionText: string;
  questionType: QuestionType;
}

export interface FormSectionInput {
  title: string;
  questions: FormQuestionInput[];
}

export interface RefinementTurn {
  adminFeedback: string;
  assistantDraft: string;
}

export interface DraftPromptRequest {
  description: string;
  category: string;
  hasMasterDocument: boolean;
  formSections: FormSectionInput[];
  /** Null on the first generate; populated on every Refine click. */
  refinementHistory: RefinementTurn[] | null;
}

export interface DraftPromptResult {
  draftedPromptBody: string;
  /** Advisory; never blocking. Listed when a placeholder doesn't resolve. */
  warnings: string[];
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  /** Includes the call that just succeeded. */
  draftsUsedToday: number;
  dailyDraftLimit: number;
}

/**
 * Thrown when the backend returns 429. Carries the parsed quota counters
 * (from the JSON body) plus the parsed Retry-After header value, so the
 * modal can render "Daily limit reached. Resets at midnight UTC." with
 * accurate numbers without a follow-up call.
 */
export class PromptBuilderQuotaError extends Error {
  status = 429;
  retryAfterSeconds: number | null;
  errors: string[];

  constructor(message: string, errors: string[], retryAfterSeconds: number | null) {
    super(message);
    this.name = "PromptBuilderQuotaError";
    this.errors = errors;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * Draft a prompt body via the AI Prompt Builder. Pass `templateId = null`
 * for new (not-yet-saved) templates — the backend has a parallel route
 * for that case.
 */
export async function draftPrompt(
  templateId: string | null,
  body: DraftPromptRequest,
  signal?: AbortSignal,
): Promise<DraftPromptResult> {
  const path =
    templateId === null
      ? `/admin/report-templates/draft-prompt`
      : `/admin/report-templates/${templateId}/draft-prompt`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson
    ? ((await response.json()) as ApiResponse<DraftPromptResult>)
    : null;

  if (response.status === 429) {
    const retryAfterRaw = response.headers.get("Retry-After");
    const retryAfter = retryAfterRaw ? Number.parseInt(retryAfterRaw, 10) : null;
    throw new PromptBuilderQuotaError(
      payload?.message ?? "Daily prompt drafting quota exceeded.",
      payload?.errors ?? [],
      Number.isFinite(retryAfter as number) ? (retryAfter as number) : null,
    );
  }

  if (!response.ok) {
    const errors = payload?.errors?.length ? payload.errors : [response.statusText];
    throw new ApiError(payload?.message ?? response.statusText, response.status, errors);
  }

  if (payload && "data" in payload && payload.data) {
    return payload.data;
  }
  throw new ApiError("Empty response from drafting service.", response.status, []);
}

export const promptBuilderApi = {
  draft: draftPrompt,
};
